import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createDraftInvoice, updateDraftInvoice } from 'services/XeroService'
import { Success } from 'utils/alerts'
import { transformFormDataToXeroFormat } from '../utils/formTransformers'
import { validateSubmissionRequirements, handleApiError } from '../utils/errorHandlers'
import { handleFileAttachments } from '../utils/fileHandlers'

/**
 * Custom hook for handling invoice submission
 * @param {string} id - Invoice ID (for edit mode)
 * @param {Object} recordData - Record data (for edit mode)
 * @param {Object} effectiveCompany - Effective company (selectedCompany || globalCompany)
 * @param {Object} globalCompany - Global company from navbar
 * @param {Object} xeroConfig - Xero configuration
 * @param {Array} customers - Available customers
 * @param {Function} handleDiscard - Function to navigate back
 */
export const useInvoiceSubmission = (id, recordData, effectiveCompany, globalCompany, xeroConfig, customers, handleDiscard) => {
    const [submitting, setSubmitting] = useState(false)
    const [createdInvoiceId, setCreatedInvoiceId] = useState(null)
    const navigate = useNavigate()

    const handleSubmit = async formData => {
        // Validate requirements
        const validation = validateSubmissionRequirements(effectiveCompany, globalCompany, xeroConfig, id, recordData)

        if (!validation.isValid) {
            return
        }

        const { companyToUse, configToUse } = validation
        setSubmitting(true)

        try {
            // Transform form data to Xero format
            const xeroInvoiceData = transformFormDataToXeroFormat(formData, customers)
            console.log('Sending invoice data to API:', xeroInvoiceData)
            console.log('Created invoice id:', createdInvoiceId)

            // Get the configuration ID to use
            const configId = configToUse.id
            let invoiceDetails
            let response

            // Check if we're in edit mode
            if (id) {
                console.log(`Edit mode: Using configuration ID: ${configId} to update invoice ${id}`)
                // Update existing invoice
                response = await updateDraftInvoice(configId, id, xeroInvoiceData)

                if (response.data.success) {
                    invoiceDetails = response.data.invoice
                    setCreatedInvoiceId(invoiceDetails.InvoiceID)
                    Success('Invoice updated successfully in Xero!')
                } else {
                    handleApiError({ response }, handleDiscard)
                    setSubmitting(false)
                    return
                }
            } else {
                // Create new invoice
                console.log(`Create mode: Using configuration ID: ${configId} for invoice creation`)
                response = await createDraftInvoice(configId, xeroInvoiceData)

                if (response.data.success) {
                    invoiceDetails = response.data.invoice
                    setCreatedInvoiceId(invoiceDetails.InvoiceID)
                    Success('Invoice created successfully in Xero!')
                } else {
                    handleApiError({ response }, handleDiscard)
                    setSubmitting(false)
                    return
                }
            }

            // If there are files to attach, handle them
            if (formData.files && formData.files.length > 0) {
                await handleFileAttachments(invoiceDetails.InvoiceID, formData.files, xeroConfig)
            }

            // Navigate back to list after a brief delay to show success message
            setTimeout(() => {
                handleDiscard()
            }, 1500)
        } catch (error) {
            handleApiError(error, handleDiscard)
        } finally {
            setSubmitting(false)
        }
    }

    return {
        submitting,
        createdInvoiceId,
        handleSubmit,
    }
}
